import {
    PaletteOptions,
    getContrastRatio,
    alpha,
    darken,
    lighten,
    getLuminance,
} from '@mui/material/styles';
import { tint } from 'polished';
import { grey as greyColors } from '@mui/material/colors';

import { IThemePalette } from 'graphql/types';

export const paletteOptions: PaletteOptions = {
    background: {
        default: '#fff',
    },
    text: {
        primary: '#303a46',
        secondary: '#7f7f7f',
    },
    grey: {
        ...greyColors,
        50: '#FBFBFB',
        100: '#F7F7F7',
        200: '#F0F0F0',
        300: '#e0e0e0',
        500: '#9c9c9c',
        600: '#878787',
        800: '#494949',
        900: '#202020',
    },
    action: {
        hover: '#fbfbfb',
        hoverOpacity: 0.03,
        selected: '#F7F7F7',
        focus: '#F7F7F7',
    },
    white: {
        main: '#FFF',
        light: '#000',
        dark: '#F7F7F7',
    },
};

export const THEME_COLORS = {
    primary: '#11b683',
    secondary: '#f46b75',
    error: '#dc3d50',
    danger: '#dc3d50',
    success: '#11b683',
    warning: '#f7c248',
};

export const COLORS = {
    ...THEME_COLORS,
    darkGrey: '#4b5468',
    purple: '#6200ee',
    yellow: '#fce9c0',
    body: '#303a46',
    title: '#303a46',
    subtitle: '#303a46',
    whiteTransparent: 'rgba(255, 255, 255, 0.5)',
    focus: '#4d90fe',
    validation: '#36a9f7',
};

export interface IColor {
    main: string;
    contrastText?: string;
    light?: string;
    dark?: string;
    accent?: string;
    bgColor?: string;
    hover?: string;
}

export type TColors = typeof COLORS;
export type TColorKeys = keyof TColors;

// Accent color is generated based on the given color, this will be darker when color is light.
function getAccentColor(color: string) {
    const isLightColor = getLuminance(color) > 0.179;

    return isLightColor ? darken(color, 0.2) : lighten(color, 0.2);
}

export function createPaletteSetup(
    palette: Partial<IThemePalette> = { __typename: 'ThemePalette' }
): PaletteOptions {
    const muiPalette = { ...paletteOptions, tonalOffset: 0.1 };

    const themePalette = Object.keys(COLORS).reduce((acc, key) => {
        const color = palette[key as keyof IThemePalette] || COLORS[key];

        acc[key] = {
            main: color,
            accent: getAccentColor(color),
            bgColor: tint(0.9, color),
            hover: alpha(color, 0.2),
            contrastText:
                getContrastRatio(color, '#fff') > 2
                    ? '#fff'
                    : muiPalette.text?.primary,
        };

        if (key === 'body' && acc.text) {
            acc.text.primary = color;
            acc.text.secondary = tint(0.3, color);
        }

        return acc;
    }, muiPalette);

    return themePalette;
}

export type TColorOptions = { [key: string]: TColorKeys | undefined };

/**
 * Color options will return an object with display name as key and
 * color key as value. These options are used for color select knob in
 * storybooks
 *
 * @returns `{ none: undefined, primary: 'primary', ... }`
 */
export const colorOptions: TColorOptions = Object.keys(COLORS).reduce(
    (acc, key) => ({
        ...acc,
        [key]: key,
    }),
    { none: undefined }
);
